import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Circle } from "lucide-react";
import { CompanyStep, ContactStep, PhoneStep, NotificationsStep } from "@/components/onboarding/DetailSteps";
import { CalendarStep, BillingStep } from "@/components/onboarding/FinalSteps";

const STEPS = [
  ["company", "Company details"], ["contact", "Authorized contact"], ["phone", "Phone setup"],
  ["notifications", "Alerts & consent"], ["calendar", "Calendar"], ["billing", "Billing"],
];

export default function Onboarding() {
  const { role, refresh } = useAuth();
  const [params] = useSearchParams();
  const [ob, setOb] = useState(null);
  const [billing, setBilling] = useState(null);
  const [active, setActive] = useState(params.get("step") || "company");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/org/onboarding").then((r) => setOb(r.data)).catch(() => {});
    api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => {});
  }, []);

  const save = async (step, body) => {
    setBusy(true);
    try {
      const r = await api.put(`/org/onboarding/${step}`, body);
      setOb(r.data);
      if (step === "company") refresh();
      toast.success("Saved");
      const i = STEPS.findIndex(([k]) => k === step);
      setActive(STEPS[i + 1][0]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!ob) return <div className="p-8 text-slate-400" data-testid="onboarding-loading">Loading…</div>;
  if (!["owner", "admin"].includes(role)) return <div className="p-8 text-slate-400" data-testid="onboarding-no-access">Only the company owner or an admin can complete onboarding.</div>;
  const d = ob.data || {};
  const body = {
    company: <CompanyStep initial={d.company} busy={busy} onSave={(v) => save("company", v)} />,
    contact: <ContactStep initial={d.contact} busy={busy} onSave={(v) => save("contact", v)} />,
    phone: <PhoneStep initial={d.phone} busy={busy} onSave={(v) => save("phone", v)} />,
    notifications: <NotificationsStep initial={d.notifications} busy={busy} onSave={(v) => save("notifications", v)} />,
    calendar: <CalendarStep connected={ob.steps.calendar} onNext={() => setActive("billing")} />,
    billing: <BillingStep billing={billing} done={ob.steps.billing} canceled={params.get("canceled") === "1"} />,
  }[active];
  const doneCount = Object.values(ob.steps).filter(Boolean).length;

  return (
    <div className="p-6 lg:p-10 max-w-5xl" data-testid="onboarding-page">
      <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-2">About 15 minutes</p>
      <h1 className="font-display text-4xl sm:text-5xl font-bold">Set up your company</h1>
      <p className="text-slate-400 mt-3 max-w-2xl">Explore the dashboard any time. Texting stays off until your own carrier registration is approved and passes the Go-Live check.</p>
      <div className="grid lg:grid-cols-[240px_1fr] gap-8 mt-10">
        <nav className="space-y-1" data-testid="onboarding-steps">
          <p className="text-xs text-slate-500 mb-3" data-testid="onboarding-progress">{doneCount} of {STEPS.length} done</p>
          {STEPS.map(([k, label]) => (
            <button key={k} onClick={() => setActive(k)} data-testid={`onboarding-nav-${k}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${active === k ? "bg-orange-500/15 text-orange-300 border border-orange-500/30" : "text-slate-300 hover:bg-slate-800 border border-transparent"}`}>
              {ob.steps[k] ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Circle className="w-4 h-4 text-slate-600" />} {label}
            </button>
          ))}
        </nav>
        <Card className="bg-slate-900 border-slate-800 p-6 sm:p-8" key={active}>
          <h2 className="font-display text-lg font-semibold text-white mb-6">{STEPS.find(([k]) => k === active)[1]}</h2>
          {body}
        </Card>
      </div>
      {ob.slug && (
        <p className="text-xs text-slate-500 mt-8" data-testid="onboarding-public-pages">
          Your public pages: <a className="text-orange-400 underline" href={`/c/${ob.slug}/sms-consent`} target="_blank" rel="noreferrer">SMS consent</a> ·{" "}
          <a className="text-orange-400 underline" href={`/c/${ob.slug}/privacy`} target="_blank" rel="noreferrer">Privacy policy</a>
        </p>
      )}
    </div>
  );
}
